import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import {
  getDeals,
  addDeal,
  editDeal,
  getDeal,
  submitDeal,
  rejectDeal,
  approveDeal,
  deleteDeal,
  getDealHistory,
  getLockedDeals,
  startLoadingDeals,
  stopLoadingDeals,
} from 'app/state/ducks/deals/actions'
import { DraftDealParams, DealParams } from 'app/models/deal-params'
import { successFailureResponse } from 'app/utils/util-functions'
import {
  coalesceThunk,
  coalesceThunkByKey,
} from 'app/state/utils/coalesce-thunk'
import { LOAD_FAILED, failed } from 'app/state/utils/load-result'

export const saveDeal =
  (savedDeal: DraftDealParams, deal?: any) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    savedDeal.fees.map((fee: any) => {
      if (fee.dayBasis === '') fee.dayBasis = null
    })
    const updated_savedDeal: DraftDealParams = {
      ...savedDeal,
      hasError: false,
      hasSuccessfulEntitySave: false,
      ErrorMessage: '',
      customer_token: config.company.toUpperCase(),
    }
    if (deal) {
      return client
        .put(`${apiServerUrl}/api/draft-deals`, updated_savedDeal)
        .then(deal => {
          dispatch(editDeal(deal.data))
          return { success: true, payload: deal.data }
        })
        .catch(e => {
          return { success: false, payload: e.response.data.error }
        })
    } else {
      return client
        .post(`${apiServerUrl}/api/draft-deals`, updated_savedDeal)
        .then(deal => {
          dispatch(addDeal(deal.data))
          return { success: true, payload: deal.data }
        })
        .catch(e => {
          return { success: false, payload: e.response.data.error }
        })
    }
  }

// The three per-state reads FAIL LOUDLY: a failure used to become [] and
// loadAllDeals then replaced the store with a partial list as if it were the
// whole — the CSV import matched rows against it and created every deal of a
// missing state anew. Now one failed read fails loadAllDeals, which leaves
// the store as it was and returns nothing, so a caller that needs the list
// can tell.
const loadApprovedDeals = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/approved-deals/`)
    .then(response => {
      return response.data
    })
}

const loadDraftDeals = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/draft-deals`)
    .then(response => {
      return response.data
    })
}

const loadSubmittedDeals = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/submitted-deals`)
    .then(response => {
      return response.data
    })
}

export const loadAllDeals = coalesceThunk(
  () => async (dispatch: Dispatch) => {
    try {
      dispatch(startLoadingDeals())
      const [draftDeals, submittedDeals, approvedDeals] = await Promise.all([
        loadDraftDeals(),
        loadSubmittedDeals(),
        loadApprovedDeals(),
      ])
      if ([draftDeals, submittedDeals, approvedDeals].some(failed)) return LOAD_FAILED

      const subDeals = submittedDeals.map((a: { deal: any }) => ({
        ...a.deal,
        status: 'Submitted',
      }))

      const allDeals = draftDeals
        .map((deal: DealParams) => ({ ...deal, status: 'Draft' }))
        .concat(subDeals)
        .concat(
          approvedDeals.map((deal: DealParams) => ({
            ...deal,
            status: 'Approved',
          })),
        )
      return dispatch(getDeals(allDeals))
    } catch (error) {
      console.log('THUNK ERROR: ', error)
      return LOAD_FAILED
    } finally {
      dispatch(stopLoadingDeals())
    }
  },
)

export const loadOneDeal = coalesceThunkByKey(
  (dealId: string, status: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const statusLower =
      status.toLowerCase() == 'terminated' ? 'locked' : status.toLowerCase()
    return client
      .get(`${apiServerUrl}/api/${statusLower}-deals/one?id=${dealId}`)
      .then(deals => {
        if (statusLower === 'submitted' || statusLower === 'locked') {
          return dispatch(getDeal({ ...deals.data.deal, status: status }))
        }
        return dispatch(getDeal({ ...deals.data, status: status }))
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  },
)

export const setOneDeal = (deal: any) => async (dispatch: Dispatch) => {
  dispatch(getDeal({ ...deal }))
}

export const submitForApproval =
  (deal: DealParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/draft-deals/submit`, { id: deal.id })
      .then(response => {
        dispatch(submitDeal(response.data))
        return { success: true, data: response.data, deal: deal, payload: deal }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const rejectSubmittedDeal =
  (deal: DealParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-deals/reject`, { id: deal.id })
      .then(response => {
        dispatch(rejectDeal(response.data))
        return { success: true, data: response.data, deal: deal }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const approveSubmittedDeal =
  (deal: DealParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-deals/approve`, { id: deal.id })
      .then(response => {
        const dealData = (response.data as any).extraData
        if (dealData) {
          dispatch(approveDeal(dealData))
        }
        return { success: true, data: response.data, deal: deal, payload: deal }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const deleteDraftDeal =
  (ids: string[]) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .delete(`${apiServerUrl}/api/draft-deals?ids=${ids}`)
      .then(response => {
        return dispatch(deleteDeal(response.data))
      })
      .catch(e => {
        console.log('THUNK ERROR', e)
      })
  }

export const loadDealHistory = coalesceThunkByKey(
  (dealId: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .get(`${apiServerUrl}/api/approved-deals/history?id=${dealId}`)
      .then(response => {
        return dispatch(getDealHistory(response.data))
      })
      .catch(error => {
        console.log('THUNK ERROR: ', error)
      })
  },
)

export const loadLockedDeals = coalesceThunk(
  () => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .get(`${apiServerUrl}/api/locked-deals`)
      .then(response => {
        const lockedDeals = response.data.map(
          (a: { deal: any; locked: string }) => ({
            ...a.deal,
            status: 'Terminated',
            terminationDate: a.locked,
          }),
        )

        return dispatch(getLockedDeals(lockedDeals))
      })
      .catch(error => {
        console.log('THUNK ERROR: ', error)
      })
  },
)

export const loadDealSnapshot = (dealId: string) => {
  const apiServerUrl = config.backendUrl
  return client
    .get(`${apiServerUrl}/api/approved-deals/snapshot?id=${dealId}`)
    .then(deals => {
      return { success: true, payload: deals.data }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}

export const createOrEditBulkDeals = (deals: DealParams[]) => async () => {
  const apiServerUrl = config.backendUrl
  console.log('deals', deals)
  return client
    .post(`${apiServerUrl}/api/draft-deals/bulk`, { dealList: deals })
    .then(response => {
      console.log('reponse', response)
      const goodResp = successFailureResponse(response.data)
      console.log('goodResp', goodResp)
      return { success: true, payload: response.data }
    })
    .catch(e => {
      console.log('deals bulk upload error', e)
      return { success: false, payload: e.response.data.error }
    })
}

export const submitBulkDeals = (deals: DealParams[]) => async () => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/draft-deals/bulk_submit`, { dealList: deals })
    .then(response => {
      console.log('response bulk', response)
      return { success: true, payload: response.data }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}

export const approveBulkDeals = (deals: DealParams[]) => async () => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/submitted-deals/bulk_approve`, {
      dealList: deals,
    })
    .then(response => {
      console.log('response bulk', response)
      return { success: true, payload: response.data }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}
